import { supabase, handleSupabaseError, handleSupabaseSuccess } from '../lib/supabase';
import { AdminUser, LoginCredentials, ApiResponse, AdminRole, AdminPermission } from '../types';

// ============================================================================
// SUPABASE AUTHENTICATION SERVICE
// ============================================================================

class AuthService {
  private static instance: AuthService;
  private currentUser: AdminUser | null = null;
  private isDemoMode: boolean = false;

  private constructor() {
    // Check if we're in demo mode (using placeholder credentials)
    this.isDemoMode = this.checkDemoMode();
    
    // Temporarily disable auth listener to fix 1-minute timeout issue
    // Initialize authentication state listener only if not in demo mode
    // if (!this.isDemoMode) {
    //   this.initializeAuthListener();
    // }
  }

  private checkDemoMode(): boolean {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    return (
      supabaseUrl === 'https://placeholder-project.supabase.co' ||
      supabaseAnonKey === 'placeholder-anon-key' ||
      (supabaseUrl?.includes('placeholder') ?? false) ||
      (supabaseAnonKey?.includes('placeholder') ?? false) ||
      !supabaseUrl ||
      !supabaseAnonKey
    );
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // ============================================================================
  // AUTHENTICATION METHODS
  // ============================================================================

  // Hardcoded admin credentials that always work
  private readonly ADMIN_CREDENTIALS = {
    'admin@learnova.com': {
      password: 'Learn@Nova2025',
      user: {
        id: 'hardcoded-super-admin',
        email: 'admin@learnova.com',
        name: 'Learnova Admin',
        role: 'super_admin' as AdminRole,
        permissions: ['users.read', 'users.write', 'users.delete', 'content.read', 'content.write', 'content.delete', 'content.moderate', 'analytics.read', 'analytics.export', 'system.admin'] as AdminPermission[],
        lastLoginDate: new Date(),
        isActive: true,
        createdDate: new Date('2024-01-01'),
        profileImage: undefined,
      }
    },
    'support@learnova.info': {
      password: 'Learn@Nova2025',
      user: {
        id: 'hardcoded-support-admin',
        email: 'support@learnova.info',
        name: 'Learnova Support',
        role: 'super_admin' as AdminRole,
        permissions: ['users.read', 'users.write', 'users.delete', 'content.read', 'content.write', 'content.delete', 'content.moderate', 'analytics.read', 'analytics.export', 'system.admin'] as AdminPermission[],
        lastLoginDate: new Date(),
        isActive: true,
        createdDate: new Date('2024-01-01'),
        profileImage: undefined,
      }
    },
  };

  /**
   * Login admin user with email and password
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<AdminUser>> {
    try {
      // First, check hardcoded admin credentials (always available)
      const hardcodedAdmin = this.ADMIN_CREDENTIALS[credentials.email as keyof typeof this.ADMIN_CREDENTIALS];
      if (hardcodedAdmin && hardcodedAdmin.password === credentials.password) {
        this.currentUser = hardcodedAdmin.user;
        localStorage.setItem('adminUser', JSON.stringify(hardcodedAdmin.user));
        return handleSupabaseSuccess(hardcodedAdmin.user, 'Login successful');
      }

      // Handle demo mode authentication
      if (this.isDemoMode) {
        return this.handleDemoLogin(credentials);
      }

      // Sign in with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (authError) {
        return handleSupabaseError<AdminUser>(authError);
      }

      if (!authData.user) {
        return {
          success: false,
          data: null as any,
          error: 'Invalid credentials'
        };
      }

      // Check if email is verified
      if (!authData.user.email_confirmed_at) {
        // Sign out the user since they haven't verified their email
        await supabase.auth.signOut();
        return {
          success: false,
          data: null as any,
          error: 'Please verify your email address before logging in. Check your inbox for the verification email.'
        };
      }

      // Get admin user profile from admin_users table
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (adminError || !adminData) {
        // If no admin profile exists, sign out and return error
        await supabase.auth.signOut();
        return {
          success: false,
          data: null as any,
          error: 'Access denied. Admin account required.'
        };
      }

      // Convert database row to AdminUser type
      const adminUser: AdminUser = {
        id: adminData.id,
        email: adminData.email,
        name: adminData.name,
        role: adminData.role,
        permissions: adminData.permissions as any[],
        lastLoginDate: adminData.last_login_date ? new Date(adminData.last_login_date) : undefined,
        isActive: adminData.is_active,
        createdDate: new Date(adminData.created_date),
        profileImage: adminData.profile_image || undefined,
      };

      // Update last login date
      await supabase
        .from('admin_users')
        .update({ last_login_date: new Date().toISOString() })
        .eq('id', adminData.id);

      this.currentUser = adminUser;
      return handleSupabaseSuccess(adminUser, 'Login successful');
    } catch (error) {
      return handleSupabaseError<AdminUser>(error);
    }
  }

  /**
   * Handle demo mode login with mock credentials
   */
  private async handleDemoLogin(credentials: LoginCredentials): Promise<ApiResponse<AdminUser>> {
    // Demo credentials
    const demoUsers = {
      'admin@learnova.com': {
        password: 'admin123',
        user: {
          id: 'demo-super-admin-id',
          email: 'admin@learnova.com',
          name: 'Super Admin',
          role: 'super_admin' as AdminRole,
          permissions: ['users.read', 'users.write', 'users.delete', 'content.read', 'content.write', 'content.delete', 'content.moderate', 'analytics.read', 'analytics.export', 'system.admin'] as AdminPermission[],
          lastLoginDate: new Date(),
          isActive: true,
          createdDate: new Date('2024-01-01'),
          profileImage: undefined,
        }
      },
      'support@learnova.info': {
        password: 'admin123',
        user: {
          id: 'demo-learnova-admin-id',
          email: 'support@learnova.info',
          name: 'Learnova Admin',
          role: 'super_admin' as AdminRole,
          permissions: ['users.read', 'users.write', 'users.delete', 'content.read', 'content.write', 'content.delete', 'content.moderate', 'analytics.read', 'analytics.export', 'system.admin'] as AdminPermission[],
          lastLoginDate: new Date(),
          isActive: true,
          createdDate: new Date('2024-01-01'),
          profileImage: undefined,
        }
      },
      'moderator@learnova.com': {
        password: 'admin123',
        user: {
          id: 'demo-moderator-id',
          email: 'moderator@learnova.com',
          name: 'Content Moderator',
          role: 'content_moderator' as AdminRole,
          permissions: ['content.read', 'content.moderate', 'analytics.read'] as AdminPermission[],
          lastLoginDate: new Date(),
          isActive: true,
          createdDate: new Date('2024-01-01'),
          profileImage: undefined,
        }
      }
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const demoUser = demoUsers[credentials.email as keyof typeof demoUsers];
    
    if (!demoUser || demoUser.password !== credentials.password) {
      return {
        success: false,
        data: null as any,
        error: `Invalid demo credentials. Available emails: ${Object.keys(demoUsers).join(', ')}. Password: admin123`
      };
    }

    this.currentUser = demoUser.user;
    // Store demo user in localStorage for persistence
    localStorage.setItem('demoUser', JSON.stringify(demoUser.user));
    return handleSupabaseSuccess(demoUser.user, 'Demo login successful');
  }

  /**
   * Logout current admin user
   */
  async logout(): Promise<void> {
    try {
      // Clear hardcoded admin user
      localStorage.removeItem('adminUser');
      
      // Handle demo mode logout
      if (this.isDemoMode) {
        localStorage.removeItem('demoUser');
        this.currentUser = null;
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Logout error:', error);
      }
      this.currentUser = null;
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if there's an error
      localStorage.removeItem('adminUser');
      this.currentUser = null;
    }
  }

  /**
   * Get current authenticated admin user
   */
  getCurrentUser(): AdminUser | null {
    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Check if current user has specific permission
   */
  hasPermission(permission: string): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.permissions.includes(permission as any);
  }

  /**
   * Check if current user has specific role
   */
  hasRole(role: string): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.role === role;
  }

  /**
   * Check if running in demo mode
   */
  isDemoModeActive(): boolean {
    return this.isDemoMode;
  }

  // ============================================================================
  // SESSION MANAGEMENT
  // ============================================================================

  /**
   * Initialize authentication state listener
   */
  private initializeAuthListener(): void {
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        this.currentUser = null;
      } else if (event === 'SIGNED_IN' && session?.user) {
        // Restore admin user data when session is restored
        await this.restoreAdminUser(session.user.id);
      }
    });
  }

  /**
   * Restore admin user data from database
   */
  private async restoreAdminUser(userId: string): Promise<void> {
    try {
      const { data: adminData, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', userId)
        .single();

      if (!error && adminData) {
        this.currentUser = {
          id: adminData.id,
          email: adminData.email,
          name: adminData.name,
          role: adminData.role,
          permissions: adminData.permissions as any[],
          lastLoginDate: adminData.last_login_date ? new Date(adminData.last_login_date) : undefined,
          isActive: adminData.is_active,
          createdDate: new Date(adminData.created_date),
          profileImage: adminData.profile_image || undefined,
        };
      }
    } catch (error) {
      console.error('Error restoring admin user:', error);
    }
  }

  /**
   * Restore authentication state from stored session
   */
  async restoreAuthState(): Promise<AdminUser | null> {
    try {
      // First check for hardcoded admin user in localStorage
      const adminUser = localStorage.getItem('adminUser');
      if (adminUser) {
        this.currentUser = JSON.parse(adminUser);
        return this.currentUser;
      }

      // In demo mode, check localStorage for demo user
      if (this.isDemoMode) {
        const demoUser = localStorage.getItem('demoUser');
        if (demoUser) {
          this.currentUser = JSON.parse(demoUser);
          return this.currentUser;
        }
        return null;
      }

      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        return null;
      }

      await this.restoreAdminUser(session.user.id);
      return this.currentUser;
    } catch (error) {
      console.error('Error restoring auth state:', error);
      return null;
    }
  }

  // ============================================================================
  // PASSWORD RESET
  // ============================================================================

  async requestPasswordReset(email: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, 'Password reset email sent');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  async updatePassword(newPassword: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, 'Password updated successfully');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }
}

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

export const Permissions = {
  USERS_READ: 'users.read',
  USERS_WRITE: 'users.write',
  USERS_DELETE: 'users.delete',
  CONTENT_READ: 'content.read',
  CONTENT_WRITE: 'content.write',
  CONTENT_DELETE: 'content.delete',
  CONTENT_MODERATE: 'content.moderate',
  ANALYTICS_READ: 'analytics.read',
  ANALYTICS_EXPORT: 'analytics.export',
  SYSTEM_ADMIN: 'system.admin'
} as const;

export const Roles = {
  SUPER_ADMIN: 'super_admin',
  CONTENT_MODERATOR: 'content_moderator',
  USER_MANAGER: 'user_manager',
  ANALYTICS_VIEWER: 'analytics_viewer'
} as const;

// ============================================================================
// ROUTE PROTECTION UTILITIES
// ============================================================================

export const requiresPermission = (permission: string) => {
  const authService = AuthService.getInstance();
  return authService.isAuthenticated() && authService.hasPermission(permission);
};

export const requiresRole = (role: string) => {
  const authService = AuthService.getInstance();
  return authService.isAuthenticated() && authService.hasRole(role);
};

export const requiresAuth = () => {
  const authService = AuthService.getInstance();
  return authService.isAuthenticated();
};

// Export singleton instance
export const authService = AuthService.getInstance(); 